import { useEffect, useState } from "react";
import { Star, Check, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import AdminLayout from "./AdminLayout";

const TABS = [["pending", "Pending"], ["approved", "Approved"], ["rejected", "Rejected"], ["", "All"]];
const badge = { pending: "bg-amber-100 text-amber-700", approved: "bg-emerald-100 text-emerald-700", rejected: "bg-red-100 text-red-700" };
const fmt = (d) => (d ? new Date(String(d).slice(0, 10)).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—");

export default function AdminReviews() {
  const [tab, setTab] = useState("pending");
  const [items, setItems] = useState(null);

  const load = () => api.get(`/admin/reviews${tab ? `?status=${tab}` : ""}`).then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { setItems(null); load(); /* eslint-disable-next-line */ }, [tab]);

  const setStatus = async (id, status) => {
    try { await api.patch(`/admin/reviews/${id}`, { status }); toast.success(status === "approved" ? "Review approved — now live on the tour" : `Review ${status}`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <AdminLayout title="Reviews">
      <div className="mb-4 flex flex-wrap gap-2">
        {TABS.map(([k, l]) => (
          <button key={l} onClick={() => setTab(k)} data-testid={`reviews-tab-${l.toLowerCase()}`} className={`rounded-full px-5 py-2 text-sm font-semibold ${tab === k ? "brand-gradient text-white" : "border border-gray-200 text-navy"}`}>{l}</button>
        ))}
      </div>
      {items === null ? <div className="h-40 skeleton rounded-2xl" />
        : items.length === 0 ? <div className="rounded-3xl border border-dashed border-gray-200 py-16 text-center text-gray-500" data-testid="reviews-empty">No {tab || ""} reviews.</div>
          : (
            <div className="space-y-3">
              {items.map((r) => (
                <div key={r.id} data-testid={`review-${r.id}`} className="rounded-2xl border border-gray-100 bg-white p-5 card-soft">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <p className="font-display text-lg font-bold text-navy">{r.tour_title}</p>
                      <p className="text-xs text-gray-400">{r.user_name} · {r.user_email} · {fmt(r.created_at)}</p>
                      <div className="mt-1.5 flex gap-0.5">{[1, 2, 3, 4, 5].map((n) => <Star key={n} className={`h-4 w-4 ${n <= r.rating ? "fill-brandorange text-brandorange" : "text-gray-200"}`} />)}</div>
                    </div>
                    <span className={`rounded-full px-3 py-1 text-xs font-bold ${badge[r.status]}`}>{r.status}</span>
                  </div>
                  {r.comment && <p className="mt-3 rounded-xl bg-gray-50 p-3 text-sm text-gray-600">{r.comment}</p>}
                  <div className="mt-3 flex gap-2">
                    {r.status !== "approved" && <button onClick={() => setStatus(r.id, "approved")} data-testid={`approve-${r.id}`} className="flex items-center gap-1.5 rounded-full bg-emerald-500 px-4 py-2 text-sm font-bold text-white hover:bg-emerald-600"><Check className="h-4 w-4" /> Approve</button>}
                    {r.status !== "rejected" && <button onClick={() => setStatus(r.id, "rejected")} data-testid={`reject-${r.id}`} className="flex items-center gap-1.5 rounded-full border border-brandred px-4 py-2 text-sm font-bold text-brandred hover:bg-red-50"><X className="h-4 w-4" /> Reject</button>}
                  </div>
                </div>
              ))}
            </div>
          )}
    </AdminLayout>
  );
}
